import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';

import { adminContent } from '../../services/api';
import type { QuestionLibraryCourseTree, RoleplayVoiceOption } from 'services/api/adminContentClient';
import { MediaAsset, MediaAssetType, QuestionType } from 'types';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ArrowUp, ChevronDown, ChevronUp, FolderTree, Link2, Plus, Search } from 'lucide-react';
import ContentSyncNotice from '../../components/admin/ContentSyncNotice';
import { useToast } from '../../components/ui/use-toast';
import { getQuestionHelperCopy } from '../../components/questions/optionHelperText';
import {
  AdminQuestion,
  AdminRoleplayPersona,
  CourseOption,
  DEFAULT_QUESTION_TYPE,
  AnswerReferenceWarning,
  DiffDetail,
  EMPTY_QUESTION,
  FlowOption,
  FIELD_LABELS,
  OPTION_REQUIRED_TYPES,
  QUESTION_TYPE_LABEL_BY_SLUG,
  QUESTION_TYPE_MEDIA_RESTRICTIONS,
  QUESTION_TYPE_OPTIONS,
  QuestionOptionEditable,
  QuestionTypeOption,
} from './QuestionLibrary/types';
import {
  getMultipleSelectLimitInputsFromMetadata,
  normalizeOptionData,
  sanitizeMetadataForQuestionType,
  stableStringify,
  stringOrEmpty,
  validateMetadataForQuestionType,
  validateOptionLabelsForQuestionType,
  type MultipleSelectLimitInputs,
  validateMultipleSelectLimitInputs,
  updateMultipleSelectMetadataWithInput,
} from './QuestionLibrary/utils';
import { QuestionCourseBrowser } from './QuestionLibrary/QuestionCourseBrowser';
import { QuestionSearchPanel } from './QuestionLibrary/QuestionSearchPanel';
import { UNSAVED_CHANGES_MESSAGE, useUnsavedChangesGuard } from './QuestionLibrary/useUnsavedChangesGuard';
import {
  type BrowserCourseId,
  type QuestionLibraryMode,
  UNASSIGNED_COURSE,
  parseBrowserCourseId,
} from './QuestionLibrary/navigatorUtils';
import { QuestionLibraryEditor } from './QuestionLibrary/QuestionLibraryEditor';
import { ScoringDefinitions } from './ScoringDefinitions';
import {
  CONTENT_TOOLS_QUERY_PARAMS,
  normalizePositiveIntegerParam,
  parsePositiveIntegerParam,
  updateContentToolsSearchParams,
} from './contentToolsQueryParams';
import { courseBuilderHref, moduleBuilderHref } from './contentToolLinks';

const DEFAULT_ROLEPLAY_VOICE_OPTIONS: RoleplayVoiceOption[] = [
  { alias: 'marin', name: 'Marin', provider: 'openai' },
  { alias: 'cedar', name: 'Cedar', provider: 'openai' },
];

const isQuestionLibraryUrl = (destination: URL, current: URL) =>
  destination.origin === current.origin
  && destination.pathname === current.pathname
  && destination.searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.tab) === 'question-library';

const QuestionLibrary: React.FC = () => {
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const isScoringDefinitionsView =
    searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.view) === 'scoring-definitions';
  const selectedRadarDefinitionKey = searchParams.get(
    CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.radarDefinition,
  );
  const [selected, setSelected] = useState<AdminQuestion | null>(null);
  const [draft, setDraft] = useState<AdminQuestion>(EMPTY_QUESTION);
  const [diffPreview, setDiffPreview] = useState<string[]>([]);
  const [diffPreviewDetails, setDiffPreviewDetails] = useState<DiffDetail[]>([]);
  const [referenceWarnings, setReferenceWarnings] = useState<AnswerReferenceWarning[]>([]);
  const [hasPreviewResult, setHasPreviewResult] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [optionList, setOptionList] = useState<QuestionOptionEditable[]>([]);
  const [questionTypes, setQuestionTypes] = useState<QuestionTypeOption[]>([]);
  const [questionTypesLoading, setQuestionTypesLoading] = useState(true);
  const [questionTypesLoadError, setQuestionTypesLoadError] = useState(false);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [flows, setFlows] = useState<FlowOption[]>([]);
  const [roleplayPersonas, setRoleplayPersonas] = useState<AdminRoleplayPersona[]>([]);
  const [roleplayVoiceOptions, setRoleplayVoiceOptions] = useState<RoleplayVoiceOption[]>(
    DEFAULT_ROLEPLAY_VOICE_OPTIONS,
  );
  const [attachFlowIds, setAttachFlowIds] = useState<string[]>([]);
  const [attachFlowRequiredById, setAttachFlowRequiredById] = useState<Record<string, boolean>>({});
  const [flowSearch, setFlowSearch] = useState<string>('');
  const [keyError, setKeyError] = useState<string | null>(null);
  const [metadataParseError, setMetadataParseError] = useState<string | null>(null);
  const [selectedQuestionTypeValue, setSelectedQuestionTypeValue] = useState<string>(DEFAULT_QUESTION_TYPE);
  const [multipleSelectLimitInputs, setMultipleSelectLimitInputs] = useState<MultipleSelectLimitInputs>({
    minSelections: '',
    maxSelections: '',
  });
  const skipMultipleSelectLimitSyncRef = useRef(false);
  const validQuestionTypeValues = useMemo(
    () => new Set(QUESTION_TYPE_OPTIONS.map((option) => option.value)),
    [],
  );
  const urlQuestionId = useMemo(
    () =>
      parsePositiveIntegerParam(
        searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.questionId),
      ),
    [searchParams],
  );
  const mode: QuestionLibraryMode =
    searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.mode) === 'search' ? 'search' : 'course';
  // Each tab mounts the first time it is shown and then stays mounted (hidden while the other tab is open), so
  // switching tabs keeps its filters, results page and open modules.
  const [visitedModes, setVisitedModes] = useState<Record<QuestionLibraryMode, boolean>>(() => ({
    course: mode === 'course',
    search: mode === 'search',
  }));
  useEffect(() => {
    setVisitedModes((current) => (current[mode] ? current : { ...current, [mode]: true }));
  }, [mode]);
  const browserCourseId = useMemo<BrowserCourseId | null>(
    () => parseBrowserCourseId(searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.courseId)),
    [searchParams],
  );
  const openModuleId = useMemo(
    () => parsePositiveIntegerParam(searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.flowId)),
    [searchParams],
  );
  const [refreshToken, setRefreshToken] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [courseTree, setCourseTree] = useState<QuestionLibraryCourseTree | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const scrollToEditorRef = useRef(false);
  // The question that was open when "New question" was clicked; its id can linger in the URL until the
  // router finishes the transition, and must not pull the editor back out of create mode.
  const leftQuestionIdRef = useRef<number | null>(null);
  // Saving selects the returned question before the router's transition commits. Keep it until the URL catches up.
  const savedQuestionIdRef = useRef<number | null>(null);
  const [editorResetToken, setEditorResetToken] = useState(0);
  // Edits that live only inside the editor (staged roleplay form values, unparsed metadata text).
  const [editorLocalDirty, setEditorLocalDirty] = useState(false);
  // Each preview request gets a generation; edits and newer requests make older responses obsolete.
  const previewGenerationRef = useRef(0);

  const questionTypeIdByName = useMemo(() => {
    const map: Record<string, number> = {};
    questionTypes.forEach(({ id, name }) => {
      if (typeof id === 'number' && typeof name === 'string') {
        map[name] = id;
      }
    });
    return map;
  }, [questionTypes]);

  const questionTypeAvailabilityError = questionTypesLoading
    ? 'Loading available question types…'
    : questionTypesLoadError
      ? 'Could not load question types. Reload this page to retry before previewing or saving.'
      : questionTypeIdByName[selectedQuestionTypeValue] === undefined
        ? `${QUESTION_TYPE_LABEL_BY_SLUG[selectedQuestionTypeValue] ?? selectedQuestionTypeValue} is not available on this site. Select another type or ask an administrator to check the deployment, then reload this page.`
        : null;

  const baselineQuestionTypeValue = useMemo(() => {
    if (!selected) {
      return DEFAULT_QUESTION_TYPE;
    }
    const typeName =
      selected.question_type?.name ??
      Object.entries(questionTypeIdByName).find(([, id]) => id === selected.question_type_id)?.[0];
    if (typeName && QUESTION_TYPE_OPTIONS.some((option) => option.value === typeName)) {
      return typeName;
    }
    return DEFAULT_QUESTION_TYPE;
  }, [questionTypeIdByName, selected]);

  const restrictedMediaTypes = useMemo<MediaAssetType[] | undefined>(() => {
    const allowed = QUESTION_TYPE_MEDIA_RESTRICTIONS[selectedQuestionTypeValue];
    return allowed && allowed.length > 0 ? allowed : undefined;
  }, [selectedQuestionTypeValue]);

  const helperCopy = useMemo(
    () => getQuestionHelperCopy(selectedQuestionTypeValue as QuestionType),
    [selectedQuestionTypeValue],
  );

  const multipleSelectLimitValidation = useMemo(
    () =>
      selectedQuestionTypeValue === 'multiple_select'
        ? validateMultipleSelectLimitInputs(multipleSelectLimitInputs, optionList.length)
        : {},
    [multipleSelectLimitInputs, optionList.length, selectedQuestionTypeValue],
  );

  const metadataValidationError = useMemo(
    () =>
      metadataParseError ??
      validateMetadataForQuestionType(draft.metadata, selectedQuestionTypeValue),
    [draft.metadata, metadataParseError, selectedQuestionTypeValue],
  );

  const optionValidationError = useMemo(
    () => validateOptionLabelsForQuestionType(selectedQuestionTypeValue, optionList),
    [optionList, selectedQuestionTypeValue],
  );

  const baselineOptionSignature = useMemo(() => {
    if (!selected?.options) {
      return '[]';
    }
    const options = (selected.options ?? []).map((option, index) => ({
      front_text: option.front_text ?? '',
      back_text: option.back_text ?? '',
      value: typeof option.value === 'number' ? option.value : index,
      is_correct: !!option.is_correct,
      is_other: !!option.is_other,
      order: option.order ?? index,
    }));
    return JSON.stringify(normalizeOptionData(options));
  }, [selected]);

  const currentOptionSignature = useMemo(
    () => JSON.stringify(normalizeOptionData(optionList)),
    [optionList],
  );

  const optionsChanged = useMemo(() => {
    if (!selected) {
      return true;
    }
    return baselineOptionSignature !== currentOptionSignature;
  }, [baselineOptionSignature, currentOptionSignature, selected]);

  const existingFlowIds = useMemo(() => {
    return new Set((selected?.flows ?? []).map((flow) => flow.flow_id));
  }, [selected?.flows]);

  const attachableFlows = useMemo(
    () => flows.filter((flow) => !existingFlowIds.has(flow.id)),
    [existingFlowIds, flows],
  );

  const filteredAttachableFlows = useMemo(() => {
    if (!flowSearch.trim()) {
      return attachableFlows;
    }
    const term = flowSearch.trim().toLowerCase();
    return attachableFlows.filter((flow) => flow.name.toLowerCase().includes(term));
  }, [attachableFlows, flowSearch]);

  const toggleAttachFlow = useCallback((flowId: number, checked: boolean | 'indeterminate') => {
    setAttachFlowIds((prev) => {
      const id = String(flowId);
      if (checked === true) {
        if (prev.includes(id)) {
          return prev;
        }
        return [...prev, id];
      }
      return prev.filter((item) => item !== id);
    });
    setAttachFlowRequiredById((prev) => {
      const id = String(flowId);
      if (checked === true) {
        if (id in prev) {
          return prev;
        }
        return { ...prev, [id]: true };
      }
      if (!(id in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const removeAttachFlow = useCallback((flowId: string) => {
    setAttachFlowIds((prev) => prev.filter((item) => item !== flowId));
    setAttachFlowRequiredById((prev) => {
      if (!(flowId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[flowId];
      return next;
    });
  }, []);

  const toggleAttachFlowRequired = useCallback((flowId: string, checked: boolean | 'indeterminate') => {
    setAttachFlowRequiredById((prev) => ({
      ...prev,
      [flowId]: checked === true,
    }));
  }, []);

  useEffect(() => {
    const nextId = questionTypeIdByName[selectedQuestionTypeValue];
    setDraft((prev) =>
      prev.question_type_id === nextId ? prev : { ...prev, question_type_id: nextId }
    );
  }, [questionTypeIdByName, selectedQuestionTypeValue]);

  useEffect(() => {
    if (selectedQuestionTypeValue !== 'multiple_select') {
      setMultipleSelectLimitInputs({ minSelections: '', maxSelections: '' });
      return;
    }

    if (skipMultipleSelectLimitSyncRef.current) {
      skipMultipleSelectLimitSyncRef.current = false;
      return;
    }

    setMultipleSelectLimitInputs(getMultipleSelectLimitInputsFromMetadata(draft.metadata));
  }, [draft.metadata, selectedQuestionTypeValue]);

  useEffect(() => {
    if (OPTION_REQUIRED_TYPES.has(selectedQuestionTypeValue)) {
      setOptionList((prev) => {
        if (prev.length > 0) {
          return prev;
        }
        return [
          {
            front_text: '',
            back_text: '',
            value: 0,
            is_correct: false,
            is_other: false,
          },
        ];
      });
    } else if (optionList.length > 0) {
      setOptionList([]);
    }
  }, [optionList.length, selectedQuestionTypeValue]);

  useEffect(() => {
    (async () => {
      try {
        const typeResponse = await adminContent.questions.listTypes();
        setQuestionTypes(typeResponse?.data ?? []);
        setQuestionTypesLoadError(false);
      } catch (error) {
        console.error('Failed to load question types', error);
        setQuestionTypesLoadError(true);
      } finally {
        setQuestionTypesLoading(false);
      }
    })();
    (async () => {
      try {
        const personaResponse = await adminContent.roleplayPersonas.list();
        setRoleplayPersonas(
          Array.isArray(personaResponse?.data)
            ? personaResponse.data as AdminRoleplayPersona[]
            : [],
        );
      } catch (error) {
        console.error('Failed to load roleplay personas', error);
      }

      try {
        const voiceResponse = await adminContent.operations.getRoleplayVoiceOptions();
        setRoleplayVoiceOptions(
          Array.isArray(voiceResponse?.data?.options)
            ? voiceResponse.data.options
            : DEFAULT_ROLEPLAY_VOICE_OPTIONS,
        );
      } catch (error) {
        console.error('Failed to load roleplay voice options', error);
        setRoleplayVoiceOptions(DEFAULT_ROLEPLAY_VOICE_OPTIONS);
      }
    })();
  }, []);

  const handleQuestionTypeSelect = useCallback((value: string) => {
    setSelectedQuestionTypeValue(value);
    if (value !== 'roleplay') {
      return;
    }
    setDraft((current) => ({
      ...current,
      metadata: {
        persona_key: roleplayPersonas[0]?.key ?? '',
        learning_objectives: [],
        actor_prompt: '',
        win_condition_prompt: '',
        starting_expression: 'at_ease',
        realtime_voice: 'marin',
        max_active_seconds: 600,
        max_learner_turns: 12,
        ...(current.metadata ?? {}),
      },
    }));
  }, [roleplayPersonas]);

  useEffect(() => {
    (async () => {
      try {
        const response = await adminContent.questions.listCourses();
        setCourses(response.data ?? []);
      } catch (error) {
        console.error('Failed to load courses', error);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const response = await adminContent.questions.listFlows();
        setFlows(response.data ?? []);
      } catch (error) {
        console.error('Failed to load flows', error);
      }
    })();
  }, []);

  useEffect(() => {
    if (
      !selectedQuestionTypeValue ||
      selectedQuestionTypeValue === DEFAULT_QUESTION_TYPE ||
      questionTypes.length === 0
    ) {
      return;
    }

    const hasBackendType = questionTypes.some(
      (type) => type?.name === selectedQuestionTypeValue,
    );

    const isKnownFrontendType = QUESTION_TYPE_OPTIONS.some(
      (option) => option.value === selectedQuestionTypeValue,
    );

    if (!hasBackendType && !isKnownFrontendType) {
      setSelectedQuestionTypeValue(DEFAULT_QUESTION_TYPE);
    }
  }, [questionTypes, selectedQuestionTypeValue]);

  const applySelectedQuestion = useCallback(
    (question: AdminQuestion) => {
      setSelected(question);
      setDiffPreview([]);
      setDiffPreviewDetails([]);
      setReferenceWarnings([]);
      setHasPreviewResult(false);
      setAttachFlowIds([]);
      setAttachFlowRequiredById({});
      setFlowSearch('');
      setKeyError(null);
      setMetadataParseError(null);
      const typeName =
        question.question_type?.name ??
        Object.entries(questionTypeIdByName).find(([, id]) => id === question.question_type_id)?.[0];
      const normalizedType =
        typeName && QUESTION_TYPE_OPTIONS.some((option) => option.value === typeName)
          ? typeName
          : DEFAULT_QUESTION_TYPE;
      setSelectedQuestionTypeValue(normalizedType);

      const nextMedia = question.media ?? null;
      const existingMetadata: Record<string, unknown> = { ...(question.metadata ?? {}) };
      const resolvedLowText = question.low_text ?? undefined;
      const resolvedHighText = question.high_text ?? undefined;

      setDraft({
        ...question,
        citations: Array.isArray(question.citations) ? [...question.citations] : [],
        media: nextMedia,
        media_asset_id: nextMedia?.id ?? question.media_asset_id ?? null,
        question_type_id:
          question.question_type?.id ??
          questionTypeIdByName[normalizedType] ??
          question.question_type_id,
        metadata: existingMetadata,
        low_text: resolvedLowText,
        high_text: resolvedHighText,
      });
      setOptionList(
        (question.options ?? [])
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((option, index) => ({
            id: option.id,
            front_text: option.front_text ?? '',
            back_text: option.back_text ?? '',
            value: typeof option.value === 'number' ? option.value : index,
            is_correct: !!option.is_correct,
            is_other: !!option.is_other,
            order: option.order ?? index,
          }))
      );
    },
    [questionTypeIdByName, setKeyError],
  );

  const resetToCreate = useCallback(() => {
    savedQuestionIdRef.current = null;
    setSelected(null);
    setDraft(EMPTY_QUESTION);
    setDiffPreview([]);
    setDiffPreviewDetails([]);
    setReferenceWarnings([]);
    setHasPreviewResult(false);
    setOptionList([]);
    setAttachFlowIds([]);
    setAttachFlowRequiredById({});
    setFlowSearch('');
    setKeyError(null);
    setMetadataParseError(null);
    setSelectedQuestionTypeValue(DEFAULT_QUESTION_TYPE);
  }, [setKeyError]);

  const handleSelect = useCallback(
    (question: AdminQuestion) => {
      savedQuestionIdRef.current = question.id ?? null;
      applySelectedQuestion(question);
      setIsCreating(false);
      setSearchParams(
        current => updateContentToolsSearchParams(current, {
          [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.questionId]: question.id ?? null,
        }),
        { replace: true },
      );
    },
    [applySelectedQuestion, setSearchParams],
  );

  const handleCreateNew = useCallback(() => {
    resetToCreate();
    setSearchParams(
      updateContentToolsSearchParams(searchParams, {
        [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.questionId]: null,
      }),
      { replace: true },
    );
  }, [resetToCreate, searchParams, setSearchParams]);

  useEffect(() => {
    const rawCourseId = searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.courseId);
    const rawFlowId = searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.flowId);
    const rawQuestionId = searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.questionId);
    const rawQuestionType = searchParams.get(
      CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.questionType,
    );

    const nextParams = updateContentToolsSearchParams(searchParams, {
      [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.courseId]:
        rawCourseId === UNASSIGNED_COURSE
          ? UNASSIGNED_COURSE
          : rawCourseId
            ? normalizePositiveIntegerParam(rawCourseId)
            : null,
      [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.flowId]:
        rawFlowId ? normalizePositiveIntegerParam(rawFlowId) : null,
      [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.questionId]:
        rawQuestionId ? normalizePositiveIntegerParam(rawQuestionId) : null,
      [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.questionType]:
        rawQuestionType && validQuestionTypeValues.has(rawQuestionType) ? rawQuestionType : null,
    });

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams, validQuestionTypeValues]);

  useEffect(() => {
    if (savedQuestionIdRef.current !== null) {
      if (urlQuestionId !== savedQuestionIdRef.current) return;
      savedQuestionIdRef.current = null;
    }
    if (urlQuestionId === null) {
      leftQuestionIdRef.current = null;
      if (selected) {
        resetToCreate();
      }
      return;
    }

    if (selected?.id === urlQuestionId || urlQuestionId === leftQuestionIdRef.current) {
      return;
    }

    let isCancelled = false;

    void (async () => {
      try {
        const response = await adminContent.questions.retrieve(urlQuestionId, { include_inactive: true });
        if (isCancelled) {
          return;
        }
        applySelectedQuestion(response.data as AdminQuestion);
        setIsCreating(false);
      } catch (error) {
        if (isCancelled) {
          return;
        }
        resetToCreate();
        setSearchParams(
          updateContentToolsSearchParams(searchParams, {
            [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.questionId]: null,
          }),
          { replace: true },
        );
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [
    applySelectedQuestion,
    resetToCreate,
    searchParams,
    selected,
    setSearchParams,
    urlQuestionId,
  ]);

  const handleInputChange = useCallback(
    (field: keyof AdminQuestion, value: unknown) => {
      setDraft((prev) => ({ ...prev, [field]: value }) as AdminQuestion);
      if (field === 'key') {
        setKeyError(null);
      }
    },
    [setKeyError],
  );

  const handleMultipleSelectLimitInputChange = useCallback(
    (field: keyof MultipleSelectLimitInputs, value: string) => {
      setMultipleSelectLimitInputs((prev) => ({ ...prev, [field]: value }));
      skipMultipleSelectLimitSyncRef.current = true;
      setDraft((prev) => ({
        ...prev,
        metadata: updateMultipleSelectMetadataWithInput(prev.metadata, field, value),
      }));
    },
    [],
  );

  const handleMediaAssetChange = useCallback(
    (asset: MediaAsset | null) => {
      setDraft((prev) => ({
        ...prev,
        media: asset,
        media_asset_id: asset?.id ?? null,
      }));
    },
    [],
  );

  useEffect(() => {
    if (!restrictedMediaTypes || restrictedMediaTypes.length === 0) {
      return;
    }
    const currentType = draft.media?.media_type;
    if (currentType && !restrictedMediaTypes.includes(currentType)) {
      handleMediaAssetChange(null);
    }
  }, [draft.media?.media_type, handleMediaAssetChange, restrictedMediaTypes]);

  const addOption = useCallback(() => {
    setOptionList((prev) => [
      ...prev,
      {
        front_text: '',
        back_text: '',
        value: prev.length,
        is_correct: false,
        is_other: false,
      },
    ]);
  }, []);

  const updateOptionField = useCallback(
    (index: number, patch: Partial<QuestionOptionEditable>) => {
      setOptionList((prev) =>
        prev.map((option, i) => (i === index ? { ...option, ...patch } : option))
      );
    },
    []
  );

  const updateOptionValue = useCallback(
    (index: number, value: string) => {
      const numeric = Number(value);
      updateOptionField(index, {
        value: Number.isFinite(numeric) ? numeric : 0,
      });
    },
    [updateOptionField]
  );

  const updateOptionFlag = useCallback(
    (index: number, field: 'is_correct' | 'is_other', checked: boolean | 'indeterminate') => {
      updateOptionField(index, { [field]: checked === true } as Partial<QuestionOptionEditable>);
    },
    [updateOptionField]
  );

  const removeOption = useCallback(
    (index: number) => {
      setOptionList((prev) => {
        const next = prev.filter((_, i) => i !== index);
        if (OPTION_REQUIRED_TYPES.has(selectedQuestionTypeValue) && next.length === 0) {
          return [
            {
              front_text: '',
              back_text: '',
              value: 0,
              is_correct: false,
              is_other: false,
            },
          ];
        }
        return next;
      });
    },
    [selectedQuestionTypeValue]
  );

  const buildOptionPayload = useCallback(() => {
    return optionList.map((option, index) => ({
      ...(option.id ? { id: option.id } : {}),
      front_text: (option.front_text ?? '').trim(),
      back_text: (option.back_text ?? '').trim(),
      value: Number.isFinite(option.value) ? option.value : index,
      order: index,
      is_correct: option.is_correct,
      is_other: option.is_other,
    }));
  }, [optionList]);


  const buildQuestionPayload = useCallback(
    (questionTypeId?: number, includeOptions: boolean = true) => {
      const normalizeOptionalText = (value?: string | null) => {
        if (value === undefined || value === null) {
          return undefined;
        }

        const trimmed = value.trim();
        return trimmed.length ? trimmed : '';
      };

      const metadata: Record<string, unknown> = sanitizeMetadataForQuestionType(
        draft.metadata,
        selectedQuestionTypeValue,
        multipleSelectLimitInputs,
      );
      const normalizedCitations = [...(draft.citations ?? [])].map((entry) =>
        typeof entry === 'string' ? entry.trim() : '',
      );
      while (normalizedCitations.length > 0 && normalizedCitations[normalizedCitations.length - 1] === '') {
        normalizedCitations.pop();
      }

      const base: Record<string, unknown> = {
        key: draft.key,
        text: draft.text,
        category: draft.category,
        metadata,
        is_active: draft.is_active ?? true,
        citations: normalizedCitations,
      };

      if (selectedQuestionTypeValue === 'additional_resources') {
        base.text = 'Additional Resources';
        base.header = '';
        base.subtext = '';
      }

      if (selectedQuestionTypeValue === 'ai_summary') {
        base.text = base.text || "Here's What Your Responses Reveal";
        base.subtext = base.subtext || 'Take a moment to read through this personalized reflection.';
      }

      base.media_asset = draft.media?.id ?? draft.media_asset_id ?? null;

      if (typeof draft.id === 'number' && draft.id > 0) {
        base.id = draft.id;
      }

      // Authored Markdown whitespace (including hard breaks) is part of the source.
      const header = draft.header ?? null;
      if (selectedQuestionTypeValue !== 'additional_resources' && header !== undefined) {
        base.header = header;
      } else if (selectedQuestionTypeValue === 'additional_resources') {
        base.header = '';
      }

      const subtext = draft.subtext ?? null;
      if (selectedQuestionTypeValue !== 'additional_resources' && subtext !== undefined) {
        base.subtext = subtext;
      } else if (selectedQuestionTypeValue === 'additional_resources') {
        base.subtext = '';
      }

      const lowText = normalizeOptionalText(draft.low_text);
      if (lowText !== undefined) {
        base.low_text = lowText;
      }

      const highText = normalizeOptionalText(draft.high_text);
      if (highText !== undefined) {
        base.high_text = highText;
      }

      if (questionTypeId !== undefined) {
        base.question_type = questionTypeId;
      }

      const shouldIncludeOptions = !selected || optionsChanged;
      if (includeOptions && selectedQuestionTypeValue === 'boolean') {
        base.options = [];
      } else if (includeOptions && shouldIncludeOptions) {
        if (OPTION_REQUIRED_TYPES.has(selectedQuestionTypeValue)) {
          const optionPayload = buildOptionPayload();
          base.options = optionPayload;
        } else if (optionList.length > 0) {
          base.options = [];
        }
      }

      return base;
    },
    [
      buildOptionPayload,
      draft,
      multipleSelectLimitInputs,
      optionList,
      optionsChanged,
      selected,
      selectedQuestionTypeValue,
    ],
  );

  const handlePreview = useCallback(async () => {
    let generation: number | null = null;
    try {
      const questionTypeId = questionTypeIdByName[selectedQuestionTypeValue];
      if (questionTypeAvailabilityError || questionTypeId === undefined) {
        addToast({
          title: 'Question type unavailable',
          description: questionTypeAvailabilityError ?? 'Reload this page to load available question types.',
          variant: 'destructive',
        });
        return false;
      }

      if (optionValidationError) {
        addToast({
          title: 'Invalid options',
          description: optionValidationError,
          variant: 'destructive',
        });
        return false;
      }

      if (metadataValidationError) {
        addToast({
          title: 'Invalid metadata',
          description: metadataValidationError,
          variant: 'destructive',
        });
        return false;
      }

      if (
        selectedQuestionTypeValue === 'multiple_select' &&
        (multipleSelectLimitValidation.minError || multipleSelectLimitValidation.maxError)
      ) {
        addToast({
          title: 'Invalid selection limits',
          description:
            multipleSelectLimitValidation.minError ??
            multipleSelectLimitValidation.maxError ??
            'Selection limits are invalid.',
          variant: 'destructive',
        });
        return false;
      }

      previewGenerationRef.current += 1;
      generation = previewGenerationRef.current;
      setHasPreviewResult(false);
      setDiffPreview([]);
      setDiffPreviewDetails([]);
      setReferenceWarnings([]);
      const payload = buildQuestionPayload(questionTypeId);
      const response = await adminContent.questions.preview(payload);
      if (generation !== previewGenerationRef.current) {
        // A newer review or a later edit superseded this response.
        return false;
      }
      const diff = Array.isArray(response.data?.diff) ? (response.data?.diff as string[]) : [];
      const detailsRaw = Array.isArray(response.data?.diff_details)
        ? (response.data?.diff_details as Array<Partial<DiffDetail>>)
        : [];
      const details = detailsRaw
        .filter((item): item is DiffDetail => typeof item?.field === 'string')
        .map((item) => ({
          field: item.field,
          before: item.before ?? null,
          after: item.after ?? null,
        }));
      const warnings = Array.isArray(response.data?.answer_reference_warnings)
        ? (response.data?.answer_reference_warnings as Array<Partial<AnswerReferenceWarning>>)
          .filter((item): item is AnswerReferenceWarning => typeof item?.message === 'string')
        : [];
      setDiffPreview(diff);
      setDiffPreviewDetails(details);
      setReferenceWarnings(warnings);
      setHasPreviewResult(true);
      return true;
    } catch (error) {
      if (generation !== null && generation !== previewGenerationRef.current) {
        return false;
      }
      console.error('Failed to preview question', error);
      setDiffPreview([]);
      setDiffPreviewDetails([]);
      setReferenceWarnings([]);
      setHasPreviewResult(false);
      addToast({ title: 'Preview failed', description: 'Could not compute diff.', variant: 'destructive' });
      return false;
    }
  }, [
    addToast,
    buildQuestionPayload,
    metadataValidationError,
    multipleSelectLimitValidation.maxError,
    multipleSelectLimitValidation.minError,
    optionValidationError,
    questionTypeIdByName,
    questionTypeAvailabilityError,
    selectedQuestionTypeValue,
  ]);

  useEffect(() => {
    previewGenerationRef.current += 1;
    setHasPreviewResult(false);
    setDiffPreview([]);
    setDiffPreviewDetails([]);
    setReferenceWarnings([]);
  }, [buildQuestionPayload]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setKeyError(null);
    try {
      const questionTypeId = questionTypeIdByName[selectedQuestionTypeValue];
      if (questionTypeAvailabilityError || questionTypeId === undefined) {
        addToast({
          title: 'Question type unavailable',
          description: questionTypeAvailabilityError ?? 'Reload this page to load available question types.',
          variant: 'destructive',
        });
        setSaving(false);
        return;
      }

      if (optionValidationError) {
        addToast({
          title: 'Invalid options',
          description: optionValidationError,
          variant: 'destructive',
        });
        setSaving(false);
        return;
      }

      if (metadataValidationError) {
        addToast({
          title: 'Invalid metadata',
          description: metadataValidationError,
          variant: 'destructive',
        });
        setSaving(false);
        return;
      }

      if (
        selectedQuestionTypeValue === 'multiple_select' &&
        (multipleSelectLimitValidation.minError || multipleSelectLimitValidation.maxError)
      ) {
        addToast({
          title: 'Invalid selection limits',
          description:
            multipleSelectLimitValidation.minError ??
            multipleSelectLimitValidation.maxError ??
            'Selection limits are invalid.',
          variant: 'destructive',
        });
        setSaving(false);
        return;
      }

      const payload = buildQuestionPayload(questionTypeId);
      // Inactive questions open from the course tree, so saving must reach them too.
      const response = await adminContent.questions.upsert(payload, { include_inactive: true });
      let saved = response.data as AdminQuestion;
      addToast({
        title: 'Question saved',
        description: saved.affected_factory_courses?.length
          ? 'Saved on the site. Affected Course Factory courses will reconcile this revision before their next build.'
          : saved.key,
      });

      const savedId = saved.id;
      if (savedId && attachFlowIds.length > 0) {
        const existingIds = new Set((saved.flows ?? []).map((flow) => flow.flow_id));
        const flowsToAttach = attachFlowIds
          .map((flowId) => Number(flowId))
          .filter((flowId) => Number.isFinite(flowId) && !existingIds.has(flowId));

        if (flowsToAttach.length > 0) {
          await Promise.all(
            flowsToAttach.map((flowId) =>
              adminContent.questions.attachFlow(savedId, {
                flow_id: flowId,
                is_required: attachFlowRequiredById[String(flowId)] ?? true,
              }, { include_inactive: true }),
            ),
          );
          const refreshed = await adminContent.questions.retrieve(savedId, { include_inactive: true });
          saved = refreshed.data as AdminQuestion;
        }
      }

      setRefreshToken((current) => current + 1);
      if (savedId) {
        handleSelect(saved);
      } else {
        resetToCreate();
      }
      setAttachFlowIds([]);
      setAttachFlowRequiredById({});
      setFlowSearch('');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const data = error.response.data as unknown;
        let message = 'Unable to save question.';
        let isKeyError = false;
        if (typeof data === 'string') {
          message = data;
        } else if (data && typeof data === 'object') {
          const keyErrors = (data as Record<string, unknown>).key;
          if (Array.isArray(keyErrors) && keyErrors.length && typeof keyErrors[0] === 'string') {
            message = keyErrors[0];
            isKeyError = true;
          } else if (typeof (data as Record<string, unknown>).detail === 'string') {
            message = (data as Record<string, unknown>).detail as string;
          } else if (typeof (data as Record<string, unknown>).error === 'string') {
            message = (data as Record<string, unknown>).error as string;
          } else {
            for (const value of Object.values(data as Record<string, unknown>)) {
              if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
                message = value[0];
                break;
              }
            }
          }
        }
        if (isKeyError) {
          setKeyError(message);
          addToast({ title: 'Duplicate key', description: message, variant: 'destructive' });
        } else {
          addToast({ title: 'Save failed', description: message, variant: 'destructive' });
        }
      } else {
        console.error('Failed to save question', error);
        addToast({ title: 'Save failed', description: 'Unable to save question.', variant: 'destructive' });
      }
    } finally {
      setSaving(false);
    }
  }, [
    addToast,
    attachFlowIds,
    attachFlowRequiredById,
    buildQuestionPayload,
    handleSelect,
    metadataValidationError,
    multipleSelectLimitValidation.maxError,
    multipleSelectLimitValidation.minError,
    optionValidationError,
    questionTypeIdByName,
    questionTypeAvailabilityError,
    resetToCreate,
    selectedQuestionTypeValue,
    setKeyError,
  ]);

  const formatFieldLabel = useCallback((field: string) => {
    const preset = FIELD_LABELS[field];
    if (preset) {
      return preset;
    }
    return field
      .split('_')
      .map((segment) => (segment ? segment[0].toUpperCase() + segment.slice(1) : segment))
      .join(' ');
  }, []);

  const summaryItems = useMemo(() => {
    if (diffPreviewDetails.length > 0) {
      return diffPreviewDetails.map((detail, index) => ({
        key: `${detail.field}-${index}`,
        label: `${formatFieldLabel(detail.field)} changed`,
      }));
    }
    return diffPreview.map((item, index) => {
      const match = item.match(/^(.+?) changed$/i);
      if (match) {
        return {
          key: `${item}-${index}`,
          label: `${formatFieldLabel(match[1])} changed`,
        };
      }
      return { key: `${item}-${index}`, label: item };
    });
  }, [diffPreview, diffPreviewDetails, formatFieldLabel]);

  const hasChanges = useMemo(() => {
    const baseline = selected ?? EMPTY_QUESTION;

    const hasFieldChanged = (baselineValue: unknown, currentValue: unknown) =>
      stringOrEmpty(baselineValue) !== stringOrEmpty(currentValue);

    if (hasFieldChanged(baseline.key, draft.key)) {
      return true;
    }
    if (hasFieldChanged(baseline.text, draft.text)) {
      return true;
    }
    if (hasFieldChanged(baseline.header, draft.header)) {
      return true;
    }
    if (hasFieldChanged(baseline.subtext, draft.subtext)) {
      return true;
    }
    if (hasFieldChanged(baseline.category, draft.category)) {
      return true;
    }
    if (hasFieldChanged(baseline.low_text, draft.low_text)) {
      return true;
    }
    if (hasFieldChanged(baseline.high_text, draft.high_text)) {
      return true;
    }

    const baselineCitationSignature = stableStringify(baseline.citations ?? []);
    const currentCitationSignature = stableStringify(draft.citations ?? []);
    if (baselineCitationSignature !== currentCitationSignature) {
      return true;
    }

    const baselineMediaId = baseline.media?.id ?? baseline.media_asset_id ?? null;
    const currentMediaId = draft.media?.id ?? draft.media_asset_id ?? null;
    if (baselineMediaId !== currentMediaId) {
      return true;
    }

    const baselineMetadataSignature = stableStringify(baseline.metadata ?? {});
    const currentMetadataSignature = stableStringify(draft.metadata ?? {});
    if (baselineMetadataSignature !== currentMetadataSignature) {
      return true;
    }

    const baselineIsActive = baseline.is_active ?? true;
    const currentIsActive = draft.is_active ?? true;
    if (baselineIsActive !== currentIsActive) {
      return true;
    }

    if (selectedQuestionTypeValue !== baselineQuestionTypeValue) {
      return true;
    }

    if (optionsChanged) {
      return true;
    }

    if (attachFlowIds.length > 0) {
      return true;
    }

    return false;
  }, [
    baselineQuestionTypeValue,
    draft,
    attachFlowIds.length,
    selected,
    selectedQuestionTypeValue,
    optionsChanged,
  ]);

  // A brand-new draft always reports changes, so for drafts compare every authored field with the empty baseline.
  const hasUnsavedWork = selected
    ? hasChanges || !!metadataParseError || editorLocalDirty
    : isCreating && (
      !!metadataParseError
      || editorLocalDirty
      || selectedQuestionTypeValue !== DEFAULT_QUESTION_TYPE
      || [draft.key, draft.text, draft.header, draft.subtext, draft.low_text, draft.high_text]
        .some((value) => stringOrEmpty(value).trim() !== '')
      || stringOrEmpty(draft.category).trim() !== stringOrEmpty(EMPTY_QUESTION.category)
      || stableStringify(draft.metadata ?? {}) !== stableStringify({})
      || (draft.citations ?? []).some((citation) => stringOrEmpty(citation).trim() !== '')
      || !!(draft.media?.id ?? draft.media_asset_id)
      || draft.is_active === false
      || optionList.some((option) =>
        stringOrEmpty(option.front_text).trim() !== ''
        || stringOrEmpty(option.back_text).trim() !== ''
        || option.is_correct
        || option.is_other)
      || attachFlowIds.length > 0
    );
  // The page's own URL updates (tabs, course, question, Radar Chart Setup) stay in the Question Library and ask
  // for themselves when they would replace the draft; anything that leaves the Question Library asks here.
  useUnsavedChangesGuard(hasUnsavedWork, { allowNavigation: isQuestionLibraryUrl });

  const confirmDiscardIfDirty = useCallback(
    () => !hasUnsavedWork || window.confirm(UNSAVED_CHANGES_MESSAGE),
    [hasUnsavedWork],
  );

  const handleModeChange = useCallback(
    (nextMode: string) => {
      setSearchParams(
        (current) =>
          updateContentToolsSearchParams(current, {
            [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.mode]: nextMode === 'search' ? 'search' : null,
          }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleBrowserCourseChange = useCallback(
    (courseId: BrowserCourseId) => {
      setSearchParams(
        (current) =>
          updateContentToolsSearchParams(current, {
            [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.courseId]: courseId,
            [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.flowId]:
              current.get(CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.courseId) === String(courseId)
                ? current.get(CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.flowId)
                : null,
          }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleTreeSelect = useCallback(
    (questionId: number, moduleId: number) => {
      if (questionId !== selected?.id && !confirmDiscardIfDirty()) {
        return;
      }
      savedQuestionIdRef.current = null;
      setIsCreating(false);
      setSearchParams(
        (current) =>
          updateContentToolsSearchParams(current, {
            [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.questionId]: questionId,
            [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.flowId]: moduleId,
          }),
        { replace: true },
      );
    },
    [confirmDiscardIfDirty, selected?.id, setSearchParams],
  );

  const handleSearchSelect = useCallback(
    (questionId: number) => {
      if (questionId !== selected?.id && !confirmDiscardIfDirty()) {
        return;
      }
      savedQuestionIdRef.current = null;
      setIsCreating(false);
      scrollToEditorRef.current = true;
      if (questionId === selected?.id) {
        editorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        scrollToEditorRef.current = false;
        return;
      }
      setSearchParams(
        (current) =>
          updateContentToolsSearchParams(current, {
            [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.questionId]: questionId,
          }),
        { replace: true },
      );
    },
    [confirmDiscardIfDirty, selected?.id, setSearchParams],
  );

  // Search results stay above the editor: once the picked question loads, scroll down to it.
  useEffect(() => {
    if (!scrollToEditorRef.current || !selected) {
      return;
    }
    scrollToEditorRef.current = false;
    editorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [selected]);

  const handleBackToResults = useCallback(() => {
    resultsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleCreateQuestion = useCallback(() => {
    if (!confirmDiscardIfDirty()) {
      return;
    }
    leftQuestionIdRef.current = selected?.id ?? null;
    handleCreateNew();
    setIsCreating(true);
    setEditorResetToken((current) => current + 1);
    setEditorLocalDirty(false);
  }, [confirmDiscardIfDirty, handleCreateNew, selected?.id]);

  // Discard also remounts the editor so its local state (raw metadata text, roleplay form) resets too.
  const handleDiscard = useCallback(() => {
    if (selected) {
      applySelectedQuestion(selected);
    } else {
      resetToCreate();
    }
    setEditorResetToken((current) => current + 1);
    setEditorLocalDirty(false);
  }, [applySelectedQuestion, resetToCreate, selected]);

  const treeSequence = useMemo(() => {
    if (!courseTree || !selected?.id) {
      return null;
    }
    const module =
      courseTree.modules.find(
        (item) => item.id === openModuleId && item.questions.some((question) => question.id === selected.id),
      )
      ?? courseTree.modules.find((item) => item.questions.some((question) => question.id === selected.id));
    if (!module) {
      return null;
    }
    const index = module.questions.findIndex((question) => question.id === selected.id);
    return { module, index };
  }, [courseTree, openModuleId, selected?.id]);

  const attachmentModuleLinks = useMemo(() => {
    if (!courseTree || browserCourseId === null || (courseTree.course?.id ?? UNASSIGNED_COURSE) !== browserCourseId) return {};
    return Object.fromEntries(courseTree.modules.map(module => [
      module.id,
      moduleBuilderHref({ courseId: browserCourseId, flowId: module.id }),
    ]));
  }, [browserCourseId, courseTree]);

  const openScoringDefinitions = useCallback(
    (definitionKey?: string | null) => {
      setSearchParams(
        (current) =>
          updateContentToolsSearchParams(current, {
            [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.view]: 'scoring-definitions',
            [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.radarDefinition]: definitionKey ?? null,
          }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closeScoringDefinitions = useCallback(() => {
    setSearchParams(
      (current) =>
        updateContentToolsSearchParams(current, {
          [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.view]: null,
          [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.radarDefinition]: null,
        }),
      { replace: true },
    );
  }, [setSearchParams]);

  const handleRadarDefinitionSelection = useCallback(
    (definitionKey: string | null) => {
      setSearchParams(
        (current) =>
          updateContentToolsSearchParams(current, {
            [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.view]: 'scoring-definitions',
            [CONTENT_TOOLS_QUERY_PARAMS.questionLibrary.radarDefinition]: definitionKey,
          }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const showEditor = !!selected || isCreating;
  const editorPlaceholder = (
    <div className="flex min-h-[16rem] flex-col items-center justify-center gap-3 rounded-3xl bg-white/30 p-8 text-center" data-testid="ql-editor-placeholder">
      <p className="text-base font-semibold text-foreground">
        {mode === 'course' ? 'Pick a question from the course' : 'Pick a question above'}
      </p>
      <p className="max-w-sm text-sm text-muted-foreground">
        {mode === 'course'
          ? 'Modules open in the order learners see them. Questions used in more than one module are marked.'
          : 'The editor opens here, under the results.'}
      </p>
      <Button type="button" variant="secondary" size="auto" className="px-4 py-2" onClick={handleCreateQuestion}>
        <Plus className="h-4 w-4" aria-hidden="true" /> New question
      </Button>
    </div>
  );
  const editor = showEditor ? (
        <Card maxWidth="none" className="min-w-0">
          <CardHeader className="space-y-3">
            {mode === 'course' && treeSequence && courseTree ? (
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground" data-testid="ql-editor-context">
                <span className="min-w-0 truncate">
                  {courseTree.course ? (
                    <Link to={courseBuilderHref(courseTree.course.id)} title="Open in Course Builder" className="underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                      {courseTree.course.name}
                    </Link>
                  ) : 'Modules not in any course'} ›{' '}
                  <Link
                    to={moduleBuilderHref({ courseId: courseTree.course?.id ?? UNASSIGNED_COURSE, flowId: treeSequence.module.id })}
                    title="Open in Module Builder"
                    className="underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {treeSequence.module.title || treeSequence.module.name}
                  </Link>{' '}
                  ›{' '}
                  <b className="font-semibold text-foreground">Step {treeSequence.index + 1} of {treeSequence.module.questions.length}</b>
                </span>
                {(selected?.flows?.length ?? 0) > 1 ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                    <Link2 className="h-3 w-3" aria-hidden="true" /> Also in {(selected?.flows?.length ?? 1) - 1} other module{(selected?.flows?.length ?? 0) > 2 ? 's' : ''}
                  </span>
                ) : null}
                <span className="flex-1" />
                <Button
                  type="button"
                  variant="secondary"
                  size="auto"
                  className="px-3 py-1.5"
                  disabled={treeSequence.index <= 0}
                  onClick={() => handleTreeSelect(treeSequence.module.questions[treeSequence.index - 1].id, treeSequence.module.id)}
                >
                  <ChevronUp className="h-4 w-4" aria-hidden="true" /> Prev
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="auto"
                  className="px-3 py-1.5"
                  disabled={treeSequence.index >= treeSequence.module.questions.length - 1}
                  onClick={() => handleTreeSelect(treeSequence.module.questions[treeSequence.index + 1].id, treeSequence.module.id)}
                >
                  Next <ChevronDown className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-3">
              <CardTitle className="flex-1" title={selected ? `Edit Question ${selected.key}` : 'Create Question'} />
              {mode === 'search' ? (
                <Button type="button" variant="secondary" size="auto" className="px-3 py-1.5" onClick={handleBackToResults}>
                  <ArrowUp className="h-4 w-4" aria-hidden="true" /> Back to results
                </Button>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ContentSyncNotice
              impacts={selected?.affected_factory_courses}
              subjectLabel="question"
            />
            <QuestionLibraryEditor
              key={`${selected?.id ?? 'new'}-${editorResetToken}`}
              helperCopy={helperCopy}
              draft={draft}
              keyError={keyError}
              selected={selected}
              attachmentModuleLinks={attachmentModuleLinks}
              selectedQuestionTypeValue={selectedQuestionTypeValue}
              questionTypeAvailabilityError={questionTypeAvailabilityError}
              onSelectQuestionType={handleQuestionTypeSelect}
              onInputChange={handleInputChange}
              metadataValidationError={metadataValidationError}
              metadataParseError={metadataParseError}
              onMetadataValidationChange={setMetadataParseError}
              optionValidationError={optionValidationError}
              onMediaAssetChange={handleMediaAssetChange}
              restrictedMediaTypes={restrictedMediaTypes}
              saving={saving}
              multipleSelectLimitInputs={multipleSelectLimitInputs}
              multipleSelectLimitValidation={multipleSelectLimitValidation}
              onMultipleSelectLimitInputChange={handleMultipleSelectLimitInputChange}
              optionList={optionList}
              onAddOption={addOption}
              onUpdateOptionField={updateOptionField}
              onUpdateOptionValue={updateOptionValue}
              onUpdateOptionFlag={updateOptionFlag}
              onRemoveOption={removeOption}
              attachableFlows={attachableFlows}
              filteredAttachableFlows={filteredAttachableFlows}
              attachFlowIds={attachFlowIds}
              attachFlowRequiredById={attachFlowRequiredById}
              onToggleAttachFlow={toggleAttachFlow}
              onToggleAttachFlowRequired={toggleAttachFlowRequired}
              onRemoveAttachFlow={removeAttachFlow}
              flowSearch={flowSearch}
              onFlowSearchChange={setFlowSearch}
              flows={flows}
              hasPreviewResult={hasPreviewResult}
              diffPreview={diffPreview}
              diffPreviewDetails={diffPreviewDetails}
              answerReferenceWarnings={referenceWarnings}
              summaryItems={summaryItems}
              formatFieldLabel={formatFieldLabel}
              hasChanges={hasChanges}
              onPreview={handlePreview}
              onSave={handleSave}
              onDiscard={handleDiscard}
              onLocalDirtyChange={setEditorLocalDirty}
              onOpenRadarDefinition={(definitionKey) => openScoringDefinitions(definitionKey)}
              roleplayPersonas={roleplayPersonas}
              roleplayVoiceOptions={roleplayVoiceOptions}
            />
          </CardContent>
        </Card>
  ) : editorPlaceholder;

  return (
    <>
      {isScoringDefinitionsView ? (
        <ScoringDefinitions
          initialDefinitionKey={selectedRadarDefinitionKey}
          onDefinitionChange={handleRadarDefinitionSelection}
          onBack={closeScoringDefinitions}
        />
      ) : null}
      {/* Hidden rather than unmounted during Radar Chart Setup, so an open draft (and its editor-only state) survives. */}
      <div className="space-y-4" hidden={isScoringDefinitionsView} data-testid="ql-workspace">
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={mode} onValueChange={handleModeChange}>
            <TabsList>
              <TabsTrigger value="course" className="gap-2 px-3" data-testid="ql-mode-course">
                <FolderTree className="h-4 w-4" aria-hidden="true" /> By course
              </TabsTrigger>
              <TabsTrigger value="search" className="gap-2 px-3" data-testid="ql-mode-search">
                <Search className="h-4 w-4" aria-hidden="true" /> Search all
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <span className="flex-1" />
          <Button type="button" variant="secondary" size="auto" className="px-4 py-2" onClick={() => openScoringDefinitions(null)}>
            Radar Chart Setup
          </Button>
          <Button type="button" size="auto" className="px-4 py-2" onClick={handleCreateQuestion}>
            <Plus className="h-4 w-4" aria-hidden="true" /> New question
          </Button>
        </div>

        <div
          className={
            mode === 'course'
              ? 'grid grid-cols-1 items-start gap-4 xl:grid-cols-[20rem_minmax(0,1fr)]'
              : 'grid grid-cols-1 gap-4'
          }
        >
          {visitedModes.course || mode === 'course' ? (
            // `contents` keeps the browser itself as the grid item, so its sticky layout still works.
            <div className={mode === 'course' ? 'contents' : undefined} hidden={mode !== 'course'}>
              <QuestionCourseBrowser
                courseId={browserCourseId}
                openModuleId={openModuleId}
                selectedQuestionId={selected?.id ?? null}
                refreshToken={refreshToken}
                onCourseChange={handleBrowserCourseChange}
                onSelectQuestion={handleTreeSelect}
                onTreeLoaded={setCourseTree}
              />
            </div>
          ) : null}
          {visitedModes.search || mode === 'search' ? (
            <div ref={resultsRef} className="min-w-0 scroll-mt-4" hidden={mode !== 'search'}>
              <QuestionSearchPanel
                selectedQuestionId={selected?.id ?? null}
                refreshToken={refreshToken}
                courses={courses}
                onSelectQuestion={handleSearchSelect}
              />
            </div>
          ) : null}
          {/* Same element in both tabs, so switching tabs never remounts the editor or drops its local state. */}
          <div ref={editorRef} className="min-w-0 scroll-mt-4">{editor}</div>
        </div>
      </div>
    </>
  );
};

export default QuestionLibrary;




